import React, { useEffect, useRef, useState } from 'react';

// Cloudflare Turnstile CAPTCHA Component
// Site Key should be added to .env as REACT_APP_TURNSTILE_SITE_KEY
// Get your keys at: https://dash.cloudflare.com/?to=/:account/turnstile

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '1x00000000000000000000AA'; // Test key

export const TurnstileCaptcha = ({ onVerify, onError, onExpire }) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Load Turnstile script if not already loaded
    if (!window.turnstile) {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.onload = () => setIsLoaded(true);
      document.head.appendChild(script);
    } else {
      setIsLoaded(true);
    }

    return () => {
      // Cleanup widget on unmount
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (e) {
          // Widget may already be removed
        }
      }
    };
  }, []);

  useEffect(() => {
    if (isLoaded && containerRef.current && window.turnstile) {
      // Render the widget
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token) => {
          if (onVerify) onVerify(token);
        },
        'error-callback': () => {
          if (onError) onError();
        },
        'expired-callback': () => {
          if (onExpire) onExpire();
        },
        theme: 'light',
        size: 'normal'
      });
    }
  }, [isLoaded, onVerify, onError, onExpire]);

  const reset = () => {
    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
    }
  };

  return (
    <div 
      ref={containerRef} 
      className="cf-turnstile flex justify-center my-4"
      data-testid="turnstile-captcha"
    />
  );
};

// Hook for easy integration
export const useTurnstile = () => {
  const [token, setToken] = useState(null);
  const [error, setError] = useState(false);

  const handleVerify = (captchaToken) => {
    setToken(captchaToken);
    setError(false);
  };

  const handleError = () => {
    setToken(null);
    setError(true);
  };

  const handleExpire = () => {
    setToken(null);
  };

  const reset = () => {
    setToken(null);
    setError(false);
  };

  return {
    token,
    error,
    isVerified: !!token,
    handleVerify,
    handleError,
    handleExpire,
    reset,
    TurnstileComponent: (props) => (
      <TurnstileCaptcha
        onVerify={handleVerify}
        onError={handleError}
        onExpire={handleExpire}
        {...props}
      />
    )
  };
};

export default TurnstileCaptcha;
